import { useEffect, useMemo, useState } from 'react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import BasicPagination from '../../components/DataTable/BasicPagination';
import {
  PreparationStationEmpty,
  PreparationStationFilters,
  PreparationStationFormModal,
  PreparationStationGrid,
  PreparationStationHeader,
  PreparationStationSkeleton,
  PreparationStationTable,
  StationAssignmentsModal,
} from '../../features/restaurant/preparationStations';
import useRestaurantPreparationStations from '../../hooks/restaurant/useRestaurantPreparationStations';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';

const RestaurantPreparationStations = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [enabled, setEnabled] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [printerId, setPrinterId] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const debouncedSearch = useDebouncedValue(search, 350);

  const stations = useRestaurantPreparationStations(branchId);

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((data) => {
        setBranches(data);

        if (data.length) {
          setBranchId(data[0].id);
        }
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  const filters = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    active: active === 'ALL' ? undefined : active === 'ACTIVE',
    enabled: enabled === 'ALL' ? undefined : enabled === 'ENABLED',
    printerId: printerId || undefined,
    page,
    size,
  }), [active, debouncedSearch, enabled, page, printerId, size]);

  useEffect(() => {
    if (branchId) void stations.load(filters);
  }, [branchId, filters]);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <PreparationStationHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          canPickBranch={canPickBranch}
          onCreate={stations.openCreate}
          onBranchChange={(id) => {
            setSearch('');
            setActive('ALL');
            setEnabled('ALL');
            setPrinterId(0);
            setPage(0);
            setBranchId(id);
          }}
        />

        <PreparationStationFilters
          search={search}
          active={active}
          enabled={enabled}
          printerId={printerId}
          printers={stations.printers}
          onSearchChange={(value) => { setSearch(value); setPage(0); }}
          onActiveChange={(value) => { setActive(value); setPage(0); }}
          onEnabledChange={(value) => { setEnabled(value); setPage(0); }}
          onPrinterChange={(value) => { setPrinterId(value); setPage(0); }}
        />

        {stations.loading ? (
          <PreparationStationSkeleton />
        ) : stations.list.length === 0 ? (
          <PreparationStationEmpty />
        ) : (
          <>
            <PreparationStationTable
              stations={stations.list}
              onEdit={stations.edit}
              onDeactivate={stations.deactivate}
              onManageAssignments={stations.manageAssignments}
            />
            <PreparationStationGrid
              stations={stations.list}
              onEdit={stations.edit}
              onDeactivate={stations.deactivate}
              onManageAssignments={stations.manageAssignments}
            />
          </>
        )}
        {stations.page && (
          <BasicPagination
            currentPage={stations.page.number}
            totalPages={stations.page.totalPages}
            totalElements={stations.page.totalElements}
            size={stations.page.size}
            first={stations.page.number === 0}
            last={stations.page.number >= stations.page.totalPages - 1}
            onPageChange={setPage}
            onSizeChange={(value) => { setSize(value); setPage(0); }}
          />
        )}
      </SectionWrapper>

      <PreparationStationFormModal
        isOpen={stations.stationModalOpen}
        form={stations.form}
        printers={stations.printers}
        saving={stations.saving}
        onClose={stations.closeStationModal}
        onChange={stations.updateField}
        onCreatePrinter={stations.createPrinterInline}
        onSave={stations.save}
      />

      <StationAssignmentsModal
        isOpen={stations.assignmentModalOpen}
        station={stations.selectedStation}
        assignments={stations.assignments}
        users={stations.users}
        form={stations.assignmentForm}
        loading={stations.loading}
        saving={stations.saving}
        onClose={stations.closeAssignments}
        onChange={stations.updateAssignmentField}
        onAssign={stations.assignUser}
        onUpdate={stations.updateAssignment}
        onDeactivate={stations.deactivateAssignment}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantPreparationStations;
